import { Form } from "../../core/Form";
import { Modal } from "../../utils/Modal";
import { Toast } from "../../utils/Toast";
import { initAdminFormControls } from "../global/initAdminFormControls";
import { CmsActionClient } from "./CmsActionClient";
import { CmsInspectorTabs } from "./components/CmsInspectorTabs";

interface InspectorCallbacks {
    onSaved: () => void;
    onPageCreated: (pageId: number) => void;
    onGroupSaved: (redirect: string) => void;
    getPageInspectorUrl: (pageId: number) => string;
}

export class CmsInspectorController {
    readonly #actionClient: CmsActionClient;
    #activeLocale: string;
    #form: Form | null = null;
    #activeTitle = "";
    #drawerTimer: number | null = null;
    #templateFieldDirty = false;
    #collectionLiveSaveTimer: number | null = null;
    #collectionLiveSaveSubmitting = false;
    #collectionLiveSaveQueued = false;
    #lastOpenUrl = "";

    constructor(
        private readonly root: HTMLElement,
        private readonly callbacks: InspectorCallbacks,
        activeLocale: string,
    ) {
        this.#activeLocale = activeLocale;
        this.#actionClient = new CmsActionClient(root);
    }

    init(): void {
        this.root.querySelector("[data-cms-inspector-close]")?.addEventListener("click", () => {
            void this.closeWithGuard();
        });
        this.root.querySelector("[data-cms-inspector-backdrop]")?.addEventListener("click", () => {
            void this.closeWithGuard();
        });
        window.addEventListener("keydown", (event) => {
            if (event.key === "Escape" && this.isOpen()) {
                void this.closeWithGuard();
            }
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s" && this.isOpen()) {
                const panel = this.query<HTMLElement>("[data-cms-template-field-inspector]");
                if (panel) {
                    event.preventDefault();
                    void this.saveTemplateFieldPanel(panel);
                }
            }
        });
    }

    async open(url: string, title: string): Promise<void> {
        const body = this.query("[data-cms-inspector-body]");
        const foot = this.query("[data-cms-inspector-foot]");
        const titleEl = this.query("[data-cms-inspector-title]");
        if (!body || !url) return;

        // 같은 섹션을 다시 여는 경우(저장 후 캔버스 재선택으로 인스펙터가 다시 렌더되는 등)
        // 사용자가 펼쳐둔 details 패널(Fill 등)이 접히지 않도록 열림 상태를 스냅샷한다.
        const sameTarget = url === this.#lastOpenUrl;
        const openPanels = sameTarget ? this.snapshotOpenPanels(body) : null;

        // 클릭 즉시 패널을 열고 로딩 상태를 표시한다. Collection 템플릿 섹션처럼 서버
        // 렌더가 느린 경우에도 프래그먼트 도착 전까지 빈 화면 대신 로더가 보인다.
        this.#activeTitle = title || "설정";
        if (titleEl) titleEl.textContent = this.#activeTitle;
        if (foot) foot.hidden = true;
        body.innerHTML = '<div class="admin-cms-inspector-loading" role="status" aria-live="polite">'
            + '<i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>'
            + '<span>설정을 불러오는 중…</span></div>';
        this.openDrawer();

        const html = await this.fetchHtml(url);
        if (html === null) {
            body.innerHTML = '<p class="admin-cms-builder__empty">설정을 불러오지 못했습니다. 다시 시도해 주세요.</p>';
            await Modal.alert("Could not load settings.");
            return;
        }

        body.innerHTML = html;
        if (foot) foot.hidden = false;

        const fresh = body.querySelector<HTMLInputElement>('input[name="_token"]')?.value;
        if (fresh) {
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.setAttribute("content", fresh);
            document.querySelectorAll<HTMLInputElement>('input[name="_token"]').forEach((input) => { input.value = fresh; });
        }

        initAdminFormControls(body);
        CmsInspectorTabs.enhance(body);
        this.bindForm(body);
        this.bindTemplateFieldInspector(body);
        this.bindBackgroundFillMode(body);
        this.setFooter(body);
        this.applyLocale();
        if (openPanels) this.restoreOpenPanels(body, openPanels);
        this.#lastOpenUrl = url;
        this.root.dispatchEvent(new CustomEvent("cms:inspector-opened", {
            bubbles: true,
            detail: { title: this.#activeTitle },
        }));
    }

    /** details 패널의 안정적인 식별 키: data-cms-* 속성 우선, 없으면 summary 텍스트. */
    private panelKey(el: HTMLDetailsElement): string {
        for (const attr of el.getAttributeNames()) {
            if (attr.startsWith("data-cms-")) return attr;
        }
        return el.querySelector("summary")?.textContent?.trim() ?? "";
    }

    private snapshotOpenPanels(scope: HTMLElement): Set<string> {
        const open = new Set<string>();
        scope.querySelectorAll<HTMLDetailsElement>("details").forEach((d) => {
            const key = this.panelKey(d);
            if (key === "") return;
            if (d.open) open.add(key);
        });
        return open;
    }

    private restoreOpenPanels(scope: HTMLElement, openKeys: Set<string>): void {
        scope.querySelectorAll<HTMLDetailsElement>("details").forEach((d) => {
            const key = this.panelKey(d);
            if (key === "") return;
            // 같은 섹션 재렌더에서만 호출되므로 열림/닫힘 상태를 그대로 복원한다.
            d.open = openKeys.has(key);
        });
    }

    /**
     * Fill 패널: 소스 필드를 입력하면 "배경 모드"가 자동으로 따라가게 한다.
     * (예: 배경색을 입력 → 모드가 '색상'으로 자동 전환 → 저장 시 값이 버려지지 않고 적용됨)
     * - 서버는 선택된 mode의 소스만 저장하므로(TX-007), mode를 필드에 맞춰 두는 게 핵심.
     * - 커스텀 Select도 함께 갱신: modeSel 값 변경 후 native `change`를 dispatch 하면
     *   Select 인스턴스의 change 리스너가 트리거 라벨을 재동기화한다(무한 루프 없음).
     * - '배경 없음'을 명시적으로 고르면 소스 입력값을 비워 실제로 배경이 사라지게 한다.
     */
    private bindBackgroundFillMode(body: HTMLElement): void {
        const panel = body.querySelector<HTMLElement>("[data-cms-background-settings]");
        if (!panel) return;
        const modeSel = panel.querySelector<HTMLSelectElement>('select[name="section_style[background][mode]"]');
        if (!modeSel) return;

        // 소스 필드 → 대상 모드 매핑
        const sources: Array<{ name: string; mode: string }> = [
            { name: "section_style[background][color][value]", mode: "color" },
            { name: "section_style[background][color][variable_id]", mode: "color" },
            { name: "section_style[background][image][url]", mode: "image" },
            { name: "section_style[background][image][variable_id]", mode: "image" },
            { name: "section_style[background][video][url]", mode: "video" },
            { name: "section_style[background][video][variable_id]", mode: "video" },
        ];
        const fieldFor = (name: string): HTMLInputElement | HTMLSelectElement | null =>
            panel.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`);

        const setMode = (mode: string): void => {
            if (modeSel.value === mode) return; // 이미 해당 모드면 재-dispatch 안 함
            modeSel.value = mode;
            modeSel.dispatchEvent(new Event("change", { bubbles: true }));
        };

        for (const { name, mode } of sources) {
            const field = fieldFor(name);
            if (!field) continue;
            const onEdit = (): void => {
                if (field.value.trim() !== "") setMode(mode);
            };
            field.addEventListener("input", onEdit);
            field.addEventListener("change", onEdit);
        }

        // '배경 없음' 선택 시 소스 값 비우기(텍스트 컬러는 배경과 무관하므로 유지)
        modeSel.addEventListener("change", () => {
            if (modeSel.value !== "none") return;
            for (const { name } of sources) {
                const field = fieldFor(name);
                if (field && field.value !== "") field.value = "";
            }
        });
    }

    async guard(run: () => void): Promise<void> {
        run();
    }

    setLocale(locale: string): void {
        this.#activeLocale = locale;
        this.applyLocale();
    }

    clear(): void {
        const body = this.query("[data-cms-inspector-body]");
        const foot = this.query("[data-cms-inspector-foot]");
        const footDel = this.query("[data-cms-foot-delete]");
        const titleEl = this.query("[data-cms-inspector-title]");
        if (body) body.innerHTML = '<p class="admin-cms-builder__empty">Select an item to edit.</p>';
        if (foot) foot.hidden = true;
        if (footDel) footDel.innerHTML = "";
        if (titleEl) titleEl.textContent = "Settings";
        this.#form = null;
        this.#activeTitle = "";
        this.close();
    }

    private bindForm(body: HTMLElement): void {
        const formEl = body.querySelector<HTMLFormElement>("form");
        this.#form = null;
        if (!formEl) return;

        const isGroupForm = formEl.dataset.cmsGroupForm === "1";
        const isPageCreate = formEl.dataset.cmsPageCreate === "1";
        const form = new Form(formEl, {
            submitButtonSelector: 'button[type="submit"][data-cms-inspector-save]',
            loaderSelector: "[data-cms-save-loader]",
            submittingText: "저장중...",
            onSuccess: (data) => {
                if (isGroupForm) {
                    const to = typeof data.redirect === "string" ? data.redirect : "";
                    this.callbacks.onGroupSaved(to);
                    return;
                }
                if (isPageCreate) {
                    const newId = typeof data.page_id === "number" ? data.page_id : parseInt(String(data.page_id ?? "0"), 10);
                    this.callbacks.onPageCreated(newId);
                    if (newId > 0) {
                        void this.open(this.callbacks.getPageInspectorUrl(newId), "Page settings");
                    } else {
                        this.clear();
                    }
                    return;
                }
                this.updateSaveState("saved");
                this.callbacks.onSaved();
            },
            onFail: () => {
                this.updateSaveState("error");
            },
            onSubmittingChange: (submitting) => {
                if (this.#collectionLiveSaveSubmitting || submitting) {
                    this.updateSaveState(submitting ? "saving" : "saved");
                }
            },
            requestFailedMessage: "요청에 실패했습니다.",
            invalidResponseMessage: "응답을 처리할 수 없습니다.",
        });
        form.init();
        this.#form = form;
        this.bindCollectionLiveSave(formEl);
    }

    private bindCollectionLiveSave(formEl: HTMLFormElement): void {
        const binding = formEl.querySelector<HTMLElement>("[data-template-binding]");
        if (!binding) return;

        const schedule = (): void => {
            this.updateSaveState("dirty");
            if (this.#collectionLiveSaveSubmitting) {
                this.#collectionLiveSaveQueued = true;
                return;
            }
            if (this.#collectionLiveSaveTimer !== null) {
                window.clearTimeout(this.#collectionLiveSaveTimer);
            }
            this.#collectionLiveSaveTimer = window.setTimeout(() => {
                this.#collectionLiveSaveTimer = null;
                this.submitCollectionLiveSave(formEl);
            }, 850);
        };

        const shouldTrack = (target: EventTarget | null): boolean => {
            const control = target instanceof HTMLElement
                ? target.closest<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input, textarea, select")
                : null;
            if (!control || !binding.contains(control)) return false;
            // 이름 없는 유틸 컨트롤(커스텀/멀티 셀렉트 검색창, 필드 브라우저 검색 input 등)은
            // 실제 저장 데이터가 아니므로 자동저장을 유발하지 않는다.
            if (!control.name || control.name === "_token" || control.name === "_method") return false;
            // 슬롯 추가용 헬퍼 select(추가 버튼 클릭 전까지 데이터 변화 없음) + 정렬/숨김 헬퍼 제외.
            return !control.matches(
                "[data-template-binding-add-slot-type], [data-template-binding-slot-order], [data-template-binding-slot-hidden]",
            );
        };

        binding.addEventListener("input", (event) => {
            if (shouldTrack(event.target)) schedule();
        });
        binding.addEventListener("change", (event) => {
            if (shouldTrack(event.target)) schedule();
        });
        binding.addEventListener("click", (event) => {
            const target = event.target as HTMLElement | null;
            if (!target?.closest("[data-template-binding-add-slot], [data-template-binding-slot-action], [data-field-value]")) return;
            window.setTimeout(schedule, 0);
        });

        formEl.addEventListener("submit", () => {
            if (this.#collectionLiveSaveTimer !== null) {
                window.clearTimeout(this.#collectionLiveSaveTimer);
                this.#collectionLiveSaveTimer = null;
            }
            this.#collectionLiveSaveSubmitting = true;
        }, { capture: true });
        formEl.addEventListener("admin:form:success", () => {
            this.#collectionLiveSaveSubmitting = false;
            if (!this.#collectionLiveSaveQueued) return;
            this.#collectionLiveSaveQueued = false;
            schedule();
        });
        formEl.addEventListener("admin:form:error", () => {
            this.#collectionLiveSaveSubmitting = false;
            this.#collectionLiveSaveQueued = false;
        });
    }

    private submitCollectionLiveSave(formEl: HTMLFormElement): void {
        if (!document.documentElement.contains(formEl)) return;
        if (this.#collectionLiveSaveSubmitting) {
            this.#collectionLiveSaveQueued = true;
            return;
        }
        this.#collectionLiveSaveQueued = false;
        this.#collectionLiveSaveSubmitting = true;
        this.updateSaveState("saving");
        // 자동저장은 조용히 — 푸터 저장 버튼 스피너/토스트를 띄우지 않고 상태 필만 갱신.
        // (Form.handleSubmit 이 requestSubmit 호출 중 동기적으로 플래그를 읽으므로 직후 제거)
        formEl.dataset.silentSubmit = "1";
        formEl.requestSubmit();
        delete formEl.dataset.silentSubmit;
    }

    private bindTemplateFieldInspector(body: HTMLElement): void {
        body.querySelectorAll<HTMLElement>("[data-cms-template-field-inspector]").forEach((panel) => {
            const save = (): void => {
                void this.saveTemplateFieldPanel(panel);
            };
            const markDirty = (): void => {
                this.#templateFieldDirty = true;
                this.updateSaveState("dirty");
            };
            panel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-template-field-control]").forEach((input) => {
                input.addEventListener("input", markDirty);
                input.addEventListener("change", markDirty);
            });
            panel.querySelector<HTMLElement>("[data-template-field-save]")?.addEventListener("click", (event) => {
                event.preventDefault();
                save();
            });
            panel.querySelector<HTMLElement>("[data-template-field-reset]")?.addEventListener("click", (event) => {
                event.preventDefault();
                panel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-template-field-control]").forEach((input) => {
                    if (input.dataset.defaultValue !== undefined) {
                        input.value = input.dataset.defaultValue;
                    }
                });
                this.#templateFieldDirty = true;
                save();
            });
        });
    }

    private async saveTemplateFieldPanel(panel: HTMLElement): Promise<void> {
        const url = panel.dataset.updateUrl ?? "";
        const fieldKey = panel.dataset.fieldKey ?? "";
        if (url === "" || fieldKey === "") return;
        this.updateSaveState("saving");
        const payload: Record<string, string> = {
            field_key: fieldKey,
            locale: panel.dataset.locale ?? this.#activeLocale,
        };
        panel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-template-field-control]").forEach((input) => {
            const key = input.dataset.templateFieldControl ?? input.name;
            if (key === "") return;
            if (input instanceof HTMLInputElement && input.type === "checkbox") {
                payload[key] = input.checked ? (input.value || "1") : "";
                return;
            }
            payload[key] = input.value;
        });
        const json = await this.#actionClient.sendForm(url, payload, "PATCH", { silent: true });
        if (json !== null) {
            this.#templateFieldDirty = false;
            this.updateSaveState("saved");
            this.callbacks.onSaved();
        } else {
            this.updateSaveState("error");
        }
    }

    private updateSaveState(state: "dirty" | "saving" | "saved" | "error"): void {
        const status = this.root.querySelector<HTMLElement>("[data-cms-save-status]");
        if (!status) return;
        status.dataset.cmsSaveState = state;
        status.textContent = {
            dirty: "저장 전 변경",
            saving: "저장중...",
            saved: "저장됨",
            error: "저장 실패",
        }[state];
    }

    private setFooter(body: HTMLElement): void {
        const formEl = body.querySelector<HTMLFormElement>("form");
        const saveBtn = this.query<HTMLButtonElement>("[data-cms-inspector-save]");
        if (formEl && saveBtn) {
            if (!formEl.id) formEl.id = "cms-inspector-form";
            saveBtn.setAttribute("form", formEl.id);
        }

        const host = this.query("[data-cms-foot-delete]");
        if (!host) return;
        host.innerHTML = "";
        const del = body.querySelector<HTMLElement>("[data-cms-modal-delete]");
        if (!del) return;

        const wrapper = del.closest(".admin-page-modal__delete-row");
        (wrapper ?? del).remove();
        host.appendChild(del);
        const isGroup = del.dataset.cmsGroup === "1";
        del.addEventListener("click", () => {
            if (isGroup) {
                void this.confirmAndReload(del.dataset.action ?? "", del.dataset.confirm ?? "Delete?");
                return;
            }
            void this.#actionClient.confirmAndSendJson(del.dataset.action ?? "", "DELETE", del.dataset.confirm ?? "Delete?")
                .then((json) => {
                    if (json !== null) {
                        this.showUndoToast(json);
                        this.clear();
                        this.callbacks.onSaved();
                    }
                });
        });
    }

    private async confirmAndReload(url: string, confirmMsg: string): Promise<void> {
        const ok = await this.#actionClient.confirmAndSend(url, "DELETE", confirmMsg);
        if (ok) this.callbacks.onGroupSaved("");
    }

    private showUndoToast(json: Record<string, unknown>): void {
        const undoUrl = typeof json.undo_url === "string" ? json.undo_url : "";
        if (undoUrl === "") return;
        Toast.success("삭제되었습니다.", {
            action: {
                label: "되돌리기",
                onClick: () => {
                    void this.restoreAndRefresh(undoUrl);
                },
            },
        });
    }

    private async restoreAndRefresh(url: string): Promise<void> {
        const ok = await this.#actionClient.restore(url);
        if (ok) this.callbacks.onSaved();
    }

    private applyLocale(): void {
        const body = this.query("[data-cms-inspector-body]");
        if (!body) return;
        body.querySelectorAll<HTMLElement>("[data-locale-tabs]").forEach((tabs) => {
            const btn = tabs.querySelector<HTMLElement>(`[data-locale-target="${this.#activeLocale}"]`);
            if (btn) btn.click();
        });
    }

    private openDrawer(): void {
        const drawer = this.query("[data-cms-inspector-drawer]");
        const backdrop = this.query("[data-cms-inspector-backdrop]");
        if (this.#drawerTimer !== null) {
            window.clearTimeout(this.#drawerTimer);
            this.#drawerTimer = null;
        }
        if (drawer) {
            drawer.hidden = false;
            drawer.setAttribute("aria-hidden", "false");
            drawer.classList.remove("is-closing");
            drawer.classList.add("is-opening");
            window.requestAnimationFrame(() => {
                drawer.classList.add("is-open");
                drawer.classList.remove("is-opening");
            });
        }
        if (backdrop && this.isCompact()) {
            backdrop.hidden = false;
            backdrop.classList.remove("is-closing");
            backdrop.classList.add("is-opening");
            window.requestAnimationFrame(() => {
                backdrop.classList.add("is-open");
                backdrop.classList.remove("is-opening");
            });
        }
        document.documentElement.classList.add("admin-cms-builder--drawer-open");
    }

    close(immediate = false): void {
        const drawer = this.query("[data-cms-inspector-drawer]");
        const backdrop = this.query("[data-cms-inspector-backdrop]");
        if (!this.isCompact()) {
            if (drawer) {
                drawer.hidden = false;
                drawer.setAttribute("aria-hidden", "false");
                drawer.classList.remove("is-open", "is-opening", "is-closing");
            }
            if (backdrop) {
                backdrop.hidden = true;
                backdrop.classList.remove("is-open", "is-opening", "is-closing");
            }
            document.documentElement.classList.remove("admin-cms-builder--drawer-open");
            return;
        }
        if (this.#drawerTimer !== null) {
            window.clearTimeout(this.#drawerTimer);
            this.#drawerTimer = null;
        }
        const finish = (): void => {
            if (drawer) {
                drawer.hidden = true;
                drawer.classList.remove("is-open", "is-opening", "is-closing");
            }
            if (backdrop) {
                backdrop.hidden = true;
                backdrop.classList.remove("is-open", "is-opening", "is-closing");
            }
        };
        if (immediate) {
            finish();
            if (drawer) drawer.setAttribute("aria-hidden", "true");
            document.documentElement.classList.remove("admin-cms-builder--drawer-open");
            return;
        }
        if (drawer) {
            drawer.setAttribute("aria-hidden", "true");
            drawer.classList.remove("is-open", "is-opening");
            drawer.classList.add("is-closing");
        }
        if (backdrop) {
            backdrop.classList.remove("is-open", "is-opening");
            backdrop.classList.add("is-closing");
        }
        document.documentElement.classList.remove("admin-cms-builder--drawer-open");
        this.#drawerTimer = window.setTimeout(() => {
            finish();
            this.#drawerTimer = null;
        }, 220);
    }

    private async closeWithGuard(): Promise<void> {
        this.close();
    }

    private isOpen(): boolean {
        const drawer = this.query("[data-cms-inspector-drawer]");
        return !!drawer && !drawer.hidden;
    }

    private isCompact(): boolean {
        return window.matchMedia("(max-width: 1180px)").matches;
    }

    private async fetchHtml(url: string): Promise<string | null> {
        try {
            const res = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } });
            if (!res.ok) return null;
            return await res.text();
        } catch {
            return null;
        }
    }

    private query<T extends HTMLElement = HTMLElement>(sel: string): T | null {
        return this.root.querySelector<T>(sel);
    }
}
